import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BatchCalculationStatusData, QueueditemerrorsData } from 'src/app/Modules/settings/models/idms/batch-calculation.model';
import { UserUtilityService } from 'src/app/services';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { UploadApiService } from 'src/app/services/uploadapi.service';

@Component({
    selector: 'app-batch-calculation-status',
    templateUrl: './batch-calculation-status.component.html',
    styleUrls: ['./batch-calculation-status.component.scss'],
})
export class BatchCalculationStatusComponent implements OnInit, OnDestroy {
    stateOptions: any[];
    StateFormGroup: FormGroup;
    batchPage: number = 1;
    itemsPerPage: number = 10;
    totalBatchCount: number = 0;

    queuedPage: number = 1;
    queuedItemsPerPage: number = 10;
    totalQueuedCount: number = 0;

    destroy$ = new Subject();
    batchList: BatchCalculationStatusData[] = [];
    queuedItemErrors: QueueditemerrorsData[] = [];
    selectedBatchId: string;
    spinerDisplay: boolean = false;

    private apiInterval?: ReturnType<typeof setInterval>;
    private intervalTime: number = 2 * 60 * 1000;
    tooltip_position = 'above';
    constructor(
        private uploadApiService: UploadApiService,
        private ngxUiLoaderService: NgxUiLoaderService,
        private userUtlity: UserUtilityService,
        private cd: ChangeDetectorRef,
        private globalEmittersService: GlobalEmittersService,
        private fb: FormBuilder,
        private documentApiService: DocumentApiService,
        private customToastService: CustomToastService,
        private transLocoService: TranslocoService
    ) {
        this.stateOptions = [
            { label: this.transLocoService.translate('SETTING_PAGE.IDMS_SETTINGS.ACTIVE'), value: 'Active' },
            { label: this.transLocoService.translate('SETTING_PAGE.IDMS_SETTINGS.COMPLETED'), value: 'Completed' },
        ];
    }

    ngOnInit(): void {
        this.StateFormGroup = this.fb.group({
            status: ['Active'],
        });

        this.fetchBatches(this.batchPage, this.StateFormGroup.get('status').value == 'Active' ? false : true);

        this.startApiInterval();

        this.globalEmittersService.pipeCodeReCalculate.pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.fetchBatches(this.batchPage, this.StateFormGroup.get('status').value == 'Active' ? false : true);
            }
        });

        this.StateFormGroup.get('status')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(async (value) => {
                if (value) {
                    await this.fetchBatches(
                        this.StateFormGroup.get('status').value == 'Active' ? (this.batchPage = 1) : this.batchPage,
                        this.StateFormGroup.get('status').value == 'Active' ? false : true
                    );
                }
            });
    }

    async fetchBatches(page: number, completed: boolean): Promise<void> {
        const start = (page - 1) * this.itemsPerPage + 1;
        const end = page * this.itemsPerPage;
        const payload = { start, end };

        this.ngxUiLoaderService.start();
        try {
            const res = await this.uploadApiService.getBatches(payload, completed).pipe(takeUntil(this.destroy$)).toPromise();

            if (res.success) {
                this.totalBatchCount = res.response.total;
                this.batchList = res.response.data;
                this.cd.detectChanges();
            } else {
                this.userUtlity.errorModelPopup(res.message);
            }
        } catch (e) {
            console.error(e);
        }
        this.ngxUiLoaderService.stop();
    }

    async onBatchPageChange(page: number): Promise<void> {
        this.batchPage = page;
        await this.fetchBatches(
            this.StateFormGroup.get('status').value == 'Active' ? (this.batchPage = 1) : page,
            this.StateFormGroup.get('status').value == 'Active' ? false : true
        );
        this.resetApiInterval();
    }

    // Handle opening a batch panel to load its queued item errors
    showPanel(batchId: string): void {
        this.selectedBatchId = batchId;
        this.queuedPage = 1;
        this.totalQueuedCount = 0;
        this.fetchQueuedErrors(this.queuedPage);
    }

    // Fetch queued errors based on selected batch
    async fetchQueuedErrors(page: number): Promise<void> {
        this.totalQueuedCount = null;
        this.queuedItemErrors = [];
        this.spinerDisplay = true;
        const start = (page - 1) * this.queuedItemsPerPage + 1;
        const end = page * this.queuedItemsPerPage;
        const payload = { start, end };

        try {
            const result = await this.uploadApiService.getqueueditemerrors(this.selectedBatchId, payload).toPromise();

            if (result.success) {
                this.totalQueuedCount = result.response.total;
                this.queuedItemErrors = result.response.data;
            } else {
                this.userUtlity.errorModelPopup(result.message);
            }
        } catch (e) {
            console.error('Error fetching queued errors:', e);
        } finally {
            this.spinerDisplay = false;
            this.cd.detectChanges();
        }
    }

    // Handle page change for queued errors
    async onQueuedPageChange(event: number): Promise<void> {
        this.queuedPage = event;
        await this.fetchQueuedErrors(event);
    }

    private startApiInterval() {
        this.apiInterval = setInterval(async () => {
            const start = (this.batchPage - 1) * this.itemsPerPage + 1;
            const end = this.batchPage * this.itemsPerPage;
            const payload = { start, end };

            try {
                const res = await this.uploadApiService
                    .getBatches(payload, this.StateFormGroup.get('status').value == 'Active' ? false : true, true)
                    .pipe(takeUntil(this.destroy$))
                    .toPromise();

                if (res.success) {
                    this.totalBatchCount = res.response.total;
                    this.batchList = res.response.data;
                } else {
                    this.userUtlity.errorModelPopup(res.message);
                }
            } catch (e) {
                console.error(e);
            }
        }, this.intervalTime);
    }

    DownloadFiles(row) {
        if (row.fileOutput) {
            this.ngxUiLoaderService.start();
            this.documentApiService.getUploadedImage([row.fileOutput], localStorage.getItem('currentInstanceId')).subscribe(
                (res: any) => {
                    if (res.success) {
                        if (res.response) {
                            const res_ = res.response[0];

                            // Allowed MIME types
                            const allowedMimeTypes = [
                                'application/pdf',
                                'application/vnd.ms-excel',
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                'image/png',
                                'image/jpeg',
                                'text/plain',
                            ];
                            if (!allowedMimeTypes.includes(res_.contentType)) {
                                throw new Error('Blocked: Invalid content type');
                            }

                            try {
                                // Decode base64 → binary
                                const byteCharacters = atob(res_.content);
                                const byteArray = new Uint8Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                    byteArray[i] = byteCharacters.charCodeAt(i);
                                }

                                // Sanitize file name
                                const rawFileName = res_.path?.split('/').pop() || 'downloaded-file';
                                const fileName = rawFileName.replace(/[^a-zA-Z0-9_\-.]/g, '_');

                                // Create blob
                                const blob = new Blob([byteArray], { type: res_.contentType });
                                const url = window.URL.createObjectURL(blob);

                                // Create download link
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = fileName;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                            } catch (error) {
                                console.error('Error decoding base64', error);
                                this.customToastService.errorToast('Invalid file data', 'Error');
                            }

                            this.ngxUiLoaderService.stop();
                        } else {
                            this.customToastService.errorToast(res.message, 'Error');
                            this.ngxUiLoaderService.stop();
                        }
                    }
                },
                (error) => {
                    this.ngxUiLoaderService.stop();
                }
            );
        } else {
            this.customToastService.warningToast('No data to download!!');
        }
    }

    private clearApiInterval() {
        if (this.apiInterval) {
            clearInterval(this.apiInterval);
            this.apiInterval = undefined;
        }
    }

    private resetApiInterval() {
        this.clearApiInterval();
        this.startApiInterval();
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.unsubscribe();
        this.clearApiInterval();
    }
}
